import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';
import { atualizarSeIntacto } from './conflitos';

/** Clientes do laboratório, em ordem alfabética. `name` guarda a fazenda. */
export const listClients = (laboratoryId) =>
  cached(`clients:${laboratoryId}`, () => run(supabase
    .from('clients')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('name')));

export const insertClient = queueable('client.insert',
  (row) => run(supabase.from('clients').insert([row])),
  {
    // A lista é ordenada por nome na consulta; a emenda reordena para o
    // cliente novo não aparecer no fim como se fosse um caso à parte.
    patchOf: (row) => ({
      chave: `clients:${row.laboratory_id}`,
      tipo: 'insere',
      linha: row,
      ordenarPor: 'name',
    }),
  });

export const newClientId = () => crypto.randomUUID();

export const updateClient = queueable('client.update',
  (clientId, updates, _laboratoryId, carimbo) =>
    atualizarSeIntacto('clients', clientId, updates, carimbo),
  {
    // `laboratoryId` só existe para montar a chave da emenda — a escrita em si
    // não precisa dele. Sem ele não há como saber em qual lista mexer.
    patchOf: (clientId, updates, laboratoryId) => (laboratoryId ? {
      chave: `clients:${laboratoryId}`,
      tipo: 'atualiza',
      id: clientId,
      campos: updates,
      ordenarPor: 'name',
    } : null),
  });

export const deleteClient = queueable('client.delete',
  (clientId) => run(supabase.from('clients').delete().eq('id', clientId)),
  {
    patchOf: (clientId, laboratoryId) => (laboratoryId ? {
      chave: `clients:${laboratoryId}`, tipo: 'remove', id: clientId,
    } : null),
  });

const limpo = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

const CAMPOS_DO_LOCAL = ['nome', 'endereco', 'cidade', 'uf', 'cep', 'link'];

export const novoLocal = (extra = {}) => ({
  id: crypto.randomUUID(),
  nome: '', endereco: '', cidade: '', uf: '', cep: '', link: '',
  cobranca: false,
  ...extra,
});

/**
 * Os locais do cliente: fazendas, sítios, escritório — onde o serviço
 * acontece e para onde vai o boleto.
 *
 * CADASTRO ANTIGO NÃO TEM `locais`. Tinha uma fazenda e um endereço soltos na
 * linha, e é lido daí: vira um local só, o de cobrança. O id desse local é o
 * do próprio cliente, e é o mesmo quando o cadastro é salvo pela tela nova —
 * então um serviço que o escolheu antes de o cadastro ser regravado continua
 * apontando para ele depois.
 */
export const locaisDoCliente = (cliente) => {
  if (Array.isArray(cliente?.locais) && cliente.locais.length > 0) return cliente.locais;
  const antigo = {
    nome: limpo(cliente?.farm_name),
    endereco: limpo(cliente?.address),
    cidade: limpo(cliente?.city),
    uf: limpo(cliente?.state),
    cep: limpo(cliente?.postal_code),
    link: limpo(cliente?.location_url),
  };
  if (!CAMPOS_DO_LOCAL.some(k => antigo[k])) return [];
  return [{ id: cliente.id, ...antigo, cobranca: true }];
};

/** O local onde o serviço acontece: o escolhido, senão o primeiro do cliente. */
export const localDoServico = (service, cliente) => {
  const locais = locaisDoCliente(cliente);
  return locais.find(l => l.id === service?.local_id) || locais[0] || null;
};

/** O endereço do boleto: o local marcado, senão o primeiro. */
export const localDeCobranca = (cliente) => {
  const locais = locaisDoCliente(cliente);
  return locais.find(l => l.cobranca) || locais[0] || null;
};

/**
 * Fazenda e cidade com que a sessão de campo (protocolo, OPU, TE) nasce.
 *
 * A fazenda só vem do local quando o serviço ESCOLHEU um — é o caso de quem
 * tem mais de uma, e aí "Águas Claras" é a resposta e o nome do cliente não.
 * Sem escolha fica o que sempre foi (`client_name`), para os relatórios de
 * quem tem uma fazenda só não mudarem de cabeçalho.
 */
export const sessaoDoLocal = (service, clients = []) => {
  const cliente = clients.find(c => c.id === service?.client_id);
  const local = localDoServico(service, cliente);
  return {
    farm: (service?.local_id && limpo(local?.nome)) || service?.client_name || '',
    city: limpo(local?.cidade) || cliente?.city || '',
  };
};

/**
 * Como um local é chamado numa lista. Sem nome, a cidade — "Duas Barras"
 * ainda distingue; "Local sem nome" é o último recurso.
 */
export const nomeDoLocal = (local) =>
  limpo(local?.nome) || limpo(local?.cidade) || limpo(local?.endereco) || 'Local sem nome';

/**
 * Prepara o cadastro para gravar.
 *
 * - Local totalmente vazio sai: é a linha que se abriu e não se preencheu.
 * - UM local de cobrança, sempre que houver local. Nenhum marcado → o primeiro;
 *   dois marcados → fica o primeiro deles.
 * - As colunas antigas (farm_name, address...) passam a ser CÓPIA: o nome do
 *   primeiro local e o endereço da cobrança. Quem ainda as lê — o app de
 *   antes do deploy, a busca, exportações — continua vendo algo coerente.
 */
export const prepararCliente = (form) => {
  const locais = (form?.locais || [])
    .map(l => ({
      ...l,
      ...Object.fromEntries(CAMPOS_DO_LOCAL.map(k => [k, limpo(l[k])])),
      uf: limpo(l.uf).toUpperCase(),
      cobranca: !!l.cobranca,
    }))
    .filter(l => CAMPOS_DO_LOCAL.some(k => l[k]));

  const marcado = Math.max(0, locais.findIndex(l => l.cobranca));
  locais.forEach((l, i) => { l.cobranca = i === marcado; });

  const cobranca = locais[marcado];
  const ou = (v) => v || null;
  return {
    ...form,
    locais,
    farm_name:    ou(locais[0]?.nome),
    address:      ou(cobranca?.endereco),
    city:         ou(cobranca?.cidade),
    state:        ou(cobranca?.uf),
    postal_code:  ou(cobranca?.cep),
    location_url: ou(locais[0]?.link),
  };
};

/**
 * Como o cliente aparece na tela: o CLIENTE em cima, onde e com quem embaixo.
 *
 * Já foi o contrário — o contato grande, porque quem olha o dashboard vai
 * ligar para alguém. Não funcionou: o mesmo contato atende vários clientes
 * (GABRIEL JUNQUEIRA responde por LUCAS, JULINHO, ADILSON e ALEXANDRE; NEZINHO
 * por três), e o dashboard virava uma coluna de nomes repetidos em que não se
 * distinguia um serviço do outro. Quem é o serviço é o cliente.
 *
 * `principal` cai para o contato quando o nome está vazio, porque um card sem
 * título é pior que um card com o nome errado.
 *
 * `secundario` junta fazenda e contato, nessa ordem, e tira o que repetiria
 * o principal: "BRUNO BRITO / BRUNO BRITO" não informa nada e ocupa duas
 * linhas.
 */
export const identidadeDoCliente = (cliente) => {
  const limpa = (v) => (v || '').trim();
  const contato = limpa(cliente?.contact);
  const nome    = limpa(cliente?.name);
  // Com várias fazendas, todas: "Sítio Boa Sorte, Águas Claras" é o que
  // distingue o cadastro de outro VICTOR VENTURA.
  const fazenda = locaisDoCliente(cliente).map(l => limpa(l.nome)).filter(Boolean).join(', ');

  const principal  = nome || contato || 'Sem nome';
  const secundario = [...new Set([fazenda, contato])]
    .filter(v => v && v !== principal)
    .join(' · ');

  return { principal, secundario };
};

/**
 * Nome do cliente de um serviço, lido do cadastro.
 *
 * `services.client_name` é cópia feita quando o serviço nasceu, e não
 * acompanha o cadastro: os dois serviços do ALENCAR CÉSAR MARTINS ZAMBONI
 * ainda dizem "Fazenda Serra Bonita". A cópia só vale quando o cadastro não
 * está à mão.
 */
export const nomeDoCliente = (service, clients = []) =>
  identidadeDoCliente(clients.find(c => c.id === service?.client_id)
    || { name: service?.client_name }).principal;

/**
 * Os recortes que a aba de Clientes oferece.
 *
 * Só atividade. Cidade e UF seriam filtros bonitos e inúteis: dos 14 clientes
 * reais, 2 têm cidade e 1 tem UF — oferecer o filtro produziria "nenhum
 * resultado" para quem tem o dado na cabeça e não no cadastro. É a mesma regra
 * dos relatórios: filtro que o conjunto não sabe responder é pior que filtro
 * nenhum.
 */
export const FILTROS_DE_CLIENTE = [
  { id: 'todos',  rotulo: 'Todos' },
  { id: 'ativos', rotulo: 'Com serviço ativo' },
  { id: 'sem',    rotulo: 'Sem serviço ativo' },
];

/**
 * Filtra e ordena a lista de clientes.
 *
 * A BUSCA COBRE OS TRÊS NOMES, e não só `name`. Metade dos cadastros guarda a
 * PESSOA em `name` e metade guarda a FAZENDA — quem procura "Nezinho" e quem
 * procura "Conceição do Pinheiro" podem estar atrás do mesmo cliente. Cidade e
 * telefone entram junto porque são o que sobra quando não se lembra do nome.
 *
 * `ativosPorCliente` chega pronto de quem já sabe contar serviço aberto; esta
 * função não conhece regra de status, e é isso que a mantém testável.
 *
 * A ordem é alfabética pelo nome que a tela MOSTRA. Ordenar por `name` quando
 * o cartão exibe o contato faz a lista parecer embaralhada.
 */
export const filtrarClientes = (clients = [], { busca = '', atividade = 'todos' } = {},
                                ativosPorCliente = {}) => {
  const q = busca.trim().toLowerCase();

  const casa = (c) => {
    if (!q) return true;
    const dosLocais = locaisDoCliente(c).flatMap(l => [l.nome, l.cidade, l.uf]);
    return [c?.name, c?.contact, c?.phone, ...dosLocais]
      .some(v => String(v || '').toLowerCase().includes(q));
  };

  const passaAtividade = (c) => {
    if (atividade === 'todos') return true;
    const ativos = Number(ativosPorCliente[c?.id]) || 0;
    return atividade === 'ativos' ? ativos > 0 : ativos === 0;
  };

  return (clients || [])
    .filter(c => casa(c) && passaAtividade(c))
    .sort((a, b) => identidadeDoCliente(a).principal
      .localeCompare(identidadeDoCliente(b).principal, 'pt-BR', { sensitivity: 'base' }));
};
